import * as AppController from '../app/app_controller.js?v=20260904f';
import { getPdfLibrary } from '../export/pdf_loader.js?v=20260706d';
import {
  screenToWorld,
  setStatus
} from '../render/plan_renderer.js?v=20260904f';
import { S } from '../app/app_state.js?v=20260817c';
import {
  markProjectFloorplanChanged,
  setProjectFloorplan
} from '../project/project_state_service.js?v=20260904f';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';
import {
  SATELLITE_MAP_MODES,
  SATELLITE_TILE_SIZE_PX,
  clampSatelliteZoom,
  createSatelliteFloorplan,
  lonLatToWorldPixel,
  normalizeSatelliteMapMode,
  satelliteMapAttribution,
  satelliteMapTileAvailable,
  satelliteMapTileMaxZoom,
  satelliteMapTileUrl,
  swisstopoSearchUrl,
  worldPixelToLonLat
} from '../map/satellite_map_service.js?v=20260706d';
import {
  DXF_UNIT_DEFINITIONS,
  cadMillimetersPerUnit,
  detectedCadUnitLabel,
  readCadFloorplanFile
} from './cad_floorplan_service.js?v=20260820d';

const SATELLITE_FINE_WHEEL_ZOOM_THRESHOLD = 120;
const SATELLITE_WHEEL_NOTCH_DELTA = 40;
const DEFAULT_SATELLITE_MAP_LOCATION = Object.freeze({
  label: 'Weberruetistrasse 9, 8833 Samstagern',
  lat: 47.19075393676758,
  lng: 8.681923866271973,
  zoom: 20
});

function bindFloorplanImport() {
    // Der DB-gesteuerte globale Menürenderer delegiert Datei- und Button-
    // Aktionen inzwischen über GLOBAL_MENU_ACTIONS. Die Funktion bleibt als
    // kompatibler App-Start-Hook erhalten, damit ältere Einbindungen nicht
    // brechen und keine parallelen/duplizierten Listener entstehen.
}

async function importFloorplanFile(file) {
    if (!file) return false;
    try {
      const ext = String(file.name || '').split('.').pop().toLowerCase();
      if (ext === 'pdf') {
        await openPdfFloorplanImport(file);
      } else if (String(file.type || '').startsWith('image/') || /^(?:png|jpe?g|gif|webp|bmp|svg|tiff?)$/.test(ext)) {
        const url = await fileToDataUrl(file);
        setProjectFloorplan({ kind: 'image', name: file.name, url, x: -3000, z: -2000, width: 6000, height: 4000, opacity: 0.5 });
      } else {
        throw new Error('Bitte eine Bild- oder PDF-Datei wählen.');
      }
      AppController.refresh(false);
      return true;
    } catch (error) {
      setStatus(`Grundriss konnte nicht geladen werden: ${error.message || error}`);
      return false;
    }
}

async function importCadFloorplanFile(file) {
    if (!file) return false;
    setStatus(`${String(file.name || 'CAD-Datei')} wird gelesen...`);
    try {
      const result = await readCadFloorplanFile(file);
      openCadFloorplanImport(result);
      setStatus(`${result.fileName} bereit zum Einfügen.`);
      return true;
    } catch (error) {
      console.error('[KS Studio] CAD-Grundriss konnte nicht geladen werden.', error);
      setStatus(`CAD-Grundriss konnte nicht geladen werden: ${error.message || error}`);
      return false;
    }
}

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error('Datei konnte nicht gelesen werden.'));
      reader.readAsDataURL(file);
    });
}

function formatCadSize(widthMm, heightMm) {
    const format = value => Math.round(value).toLocaleString('de-CH');
    return `${format(widthMm)} × ${format(heightMm)} mm`;
}

function createCadImportModal(result) {
    const root = document.createElement('div');
    root.className = 'modal-backdrop';
    const unitOptions = DXF_UNIT_DEFINITIONS.map(unit => (
      `<option value="${escapeHtml(unit.code)}">${escapeHtml(unit.label)}</option>`
    )).join('');
    const ignored = result.vector.unsupportedTypes.length
      ? `Nicht dargestellte Elementtypen: ${result.vector.unsupportedTypes.join(', ')}`
      : 'Alle erkannten 2D-Elemente konnten dargestellt werden.';
    root.innerHTML = `
      <div class="modal cad-floorplan-import-modal" role="dialog" aria-modal="true" aria-labelledby="cadFloorplanImportTitle">
        <div class="cad-floorplan-import-header">
          <div>
            <div id="cadFloorplanImportTitle" class="modal-title">CAD-Grundriss importieren</div>
            <div class="small-text">${escapeHtml(result.fileName)} · ${escapeHtml(result.sourceFormat.toUpperCase())}</div>
          </div>
          <button type="button" class="btn" data-action="cancel">Abbrechen</button>
        </div>
        <div class="cad-floorplan-preview-frame">
          <img src="${result.vector.url}" alt="CAD-Grundrissvorschau">
        </div>
        <div class="cad-floorplan-import-controls">
          <label>Zeichnungseinheit
            <select data-cad-unit>${unitOptions}</select>
          </label>
          <div class="cad-floorplan-size" data-cad-size></div>
          <button type="button" class="btn primary" data-action="insert">Einfügen</button>
        </div>
        <div class="small-text note cad-floorplan-import-message" data-cad-message></div>
        <div class="small-text">${result.vector.entityCount} CAD-Elemente · ${result.vector.pathCount} Linienzüge. ${escapeHtml(ignored)}</div>
      </div>
    `;
    return {
      root,
      unitSelect: root.querySelector('[data-cad-unit]'),
      size: root.querySelector('[data-cad-size]'),
      message: root.querySelector('[data-cad-message]'),
      insertBtn: root.querySelector('[data-action="insert"]'),
      cancelBtn: root.querySelector('[data-action="cancel"]')
    };
}

function openCadFloorplanImport(result) {
    const modal = createCadImportModal(result);
    const updateSize = () => {
      const factor = cadMillimetersPerUnit(result.drawing, modal.unitSelect.value);
      const widthMm = result.vector.widthUnits * factor;
      const heightMm = result.vector.heightUnits * factor;
      modal.size.textContent = formatCadSize(widthMm, heightMm);
      modal.message.textContent = modal.unitSelect.value === 'auto'
        ? `Erkannte Einheit: ${detectedCadUnitLabel(result.drawing)}.`
        : 'Die gewählte Einheit wird auf die gesamte Zeichnung angewendet.';
      modal.insertBtn.disabled = !Number.isFinite(widthMm) || !Number.isFinite(heightMm) || widthMm <= 0 || heightMm <= 0;
    };
    const insert = () => {
      const unitCode = modal.unitSelect.value;
      const factor = cadMillimetersPerUnit(result.drawing, unitCode);
      const width = result.vector.widthUnits * factor;
      const height = result.vector.heightUnits * factor;
      if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
        modal.message.textContent = 'Die CAD-Abmessungen sind ungültig. Bitte eine andere Zeichnungseinheit wählen.';
        return;
      }
      setProjectFloorplan({
        kind: result.sourceFormat,
        format: result.sourceFormat,
        name: result.fileName,
        url: result.vector.url,
        x: -width / 2,
        z: -height / 2,
        width,
        height,
        opacity: 0.5,
        cadUnit: unitCode,
        cadDetectedUnit: detectedCadUnitLabel(result.drawing),
        cadEntityCount: result.vector.entityCount
      });
      modal.root.remove();
      setStatus(`CAD-Grundriss eingefügt · ${formatCadSize(width, height)}`);
      AppController.refresh(false);
    };
    modal.unitSelect.addEventListener('change', updateSize);
    modal.insertBtn.addEventListener('click', insert);
    modal.cancelBtn.addEventListener('click', () => modal.root.remove());
    modal.root.addEventListener('keydown', event => {
      if (event.key !== 'Enter' || event.target === modal.unitSelect) return;
      event.preventDefault();
      insert();
    });
    document.body.appendChild(modal.root);
    updateSize();
    modal.unitSelect.focus();
}

function loadSatelliteTileImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Satellitenkachel konnte nicht geladen werden: ${url}`));
      img.src = url;
    });
}

async function createSatelliteSelectionSnapshotDataUrl(state, floorplan) {
    const corners = state.selectionCorners;
    if (!corners) throw new Error('Kein Kartenausschnitt gewählt.');
    const tileMode = normalizeSatelliteMapMode(state.mapMode);
    const zoom = satelliteMapTileMaxZoom(tileMode);
    const nw = lonLatToWorldPixel(corners.northWest.lat, corners.northWest.lng, zoom);
    const se = lonLatToWorldPixel(corners.southEast.lat, corners.southEast.lng, zoom);
    const sourceLeft = Math.min(nw.x, se.x);
    const sourceTop = Math.min(nw.y, se.y);
    const sourceRight = Math.max(nw.x, se.x);
    const sourceBottom = Math.max(nw.y, se.y);
    const sourceWidth = Math.max(1, sourceRight - sourceLeft);
    const sourceHeight = Math.max(1, sourceBottom - sourceTop);
    const imageWidth = Math.max(1, Math.round(Number(floorplan.imageWidthPx) || 1200));
    const imageHeight = Math.max(1, Math.round(Number(floorplan.imageHeightPx) || 800));
    floorplan.imageWidthPx = imageWidth;
    floorplan.imageHeightPx = imageHeight;
    const canvas = document.createElement('canvas');
    canvas.width = imageWidth;
    canvas.height = imageHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Kartenausschnitt konnte nicht gezeichnet werden.');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const minTileX = Math.floor(sourceLeft / SATELLITE_TILE_SIZE_PX);
    const maxTileX = Math.floor((sourceRight - 0.001) / SATELLITE_TILE_SIZE_PX);
    const minTileY = Math.floor(sourceTop / SATELLITE_TILE_SIZE_PX);
    const maxTileY = Math.floor((sourceBottom - 0.001) / SATELLITE_TILE_SIZE_PX);
    const tileCount = 2 ** zoom;
    const drawJobs = [];
    for (let tileY = minTileY; tileY <= maxTileY; tileY += 1) {
      if (tileY < 0 || tileY >= tileCount) continue;
      for (let tileX = minTileX; tileX <= maxTileX; tileX += 1) {
        if (!satelliteMapTileAvailable(tileMode, zoom, tileX, tileY)) continue;
        const wrappedTileX = ((tileX % tileCount) + tileCount) % tileCount;
        const tileLeft = tileX * SATELLITE_TILE_SIZE_PX;
        const tileTop = tileY * SATELLITE_TILE_SIZE_PX;
        const dx = ((tileLeft - sourceLeft) / sourceWidth) * imageWidth;
        const dy = ((tileTop - sourceTop) / sourceHeight) * imageHeight;
        const dw = (SATELLITE_TILE_SIZE_PX / sourceWidth) * imageWidth;
        const dh = (SATELLITE_TILE_SIZE_PX / sourceHeight) * imageHeight;
        drawJobs.push(
          loadSatelliteTileImage(satelliteMapTileUrl(tileMode, zoom, wrappedTileX, tileY))
            .then(img => ctx.drawImage(img, dx, dy, dw, dh))
            .catch(error => console.warn('[KS Studio] Kartenkachel konnte nicht gezeichnet werden.', error))
        );
      }
    }
    if (!drawJobs.length) throw new Error('Der gewählte Ausschnitt liegt ausserhalb der Kartenabdeckung.');
    await Promise.all(drawJobs);
    return canvas.toDataURL('image/png');
}

function startFloorplanRemeasure(referenceMm) {
    const fp = S.project.floorplan;
    const ref = Number(referenceMm);
    if (!fp) return;
    if (!Number.isFinite(ref) || ref <= 0) {
      setStatus('Bitte zuerst ein gültiges Referenzmass in mm eingeben.');
      return;
    }

    S.floorplanMeasure = {
      active: true,
      referenceMm: ref,
      points: []
    };
    setStatus('Grundriss neu vermessen: ersten Referenzpunkt anklicken.');
    AppController.refresh(false);
}

function cancelFloorplanRemeasure() {
    S.floorplanMeasure = { active: false, referenceMm: '', points: [] };
    setStatus('Grundriss-Neuvermessung abgebrochen.');
    AppController.refresh(false);
}

function handleFloorplanMeasureClick(evt) {
    const measure = S.floorplanMeasure;
    const fp = S.project.floorplan;
    if (!measure?.active || !fp) return false;

    evt.stopPropagation();
    const svg = document.getElementById('planSvg');
    const rect = svg.getBoundingClientRect();
    const world = screenToWorld(evt.clientX - rect.left, evt.clientY - rect.top);

    if (!pointInsideFloorplan(world, fp)) {
      setStatus('Bitte einen Punkt innerhalb des eingefügten Grundrisses anklicken.');
      return true;
    }

    measure.points.push(world);
    if (measure.points.length === 1) {
      setStatus('Grundriss neu vermessen: zweiten Referenzpunkt anklicken.');
      AppController.refresh(false);
      return true;
    }

    applyFloorplanRemeasure();
    return true;
}

function pointInsideFloorplan(point, fp) {
    return (
      point.x >= fp.x &&
      point.x <= fp.x + fp.width &&
      point.z >= fp.z &&
      point.z <= fp.z + fp.height
    );
}

function applyFloorplanRemeasure() {
    const fp = S.project.floorplan;
    const measure = S.floorplanMeasure;
    if (!fp || !measure?.active || measure.points.length < 2) return;

    const a = measure.points[0];
    const b = measure.points[1];
    const currentDistance = Math.hypot(b.x - a.x, b.z - a.z);
    const referenceMm = Number(measure.referenceMm);
    if (!Number.isFinite(referenceMm) || referenceMm <= 0 || currentDistance <= 0.5) {
      setStatus('Referenzmass oder Punktabstand ist ungültig.');
      return;
    }

    const relX = (a.x - fp.x) / fp.width;
    const relZ = (a.z - fp.z) / fp.height;
    const scale = referenceMm / currentDistance;
    const nextWidth = fp.width * scale;
    const nextHeight = fp.height * scale;

    fp.width = nextWidth;
    fp.height = nextHeight;
    fp.x = a.x - relX * nextWidth;
    fp.z = a.z - relZ * nextHeight;
    fp.referenceMm = referenceMm;

    S.floorplanMeasure = { active: false, referenceMm, points: [] };
    markProjectFloorplanChanged('floorplan-remeasure');
    setStatus(`Grundriss neu vermessen · Massstab ${Math.round(nextWidth)} × ${Math.round(nextHeight)} mm`);
    AppController.refresh(false);
}

async function openPdfFloorplanImport(file) {
    const url = URL.createObjectURL(file);
    const pdfjs = await getPdfLibrary();

    const pdf = await pdfjs.getDocument(url).promise;
    const page = await pdf.getPage(1);
    const modal = createPdfImportModal(file.name);
    const state = {
      fileName: file.name,
      page,
      rotation: 0,
      imageCanvas: null,
      imageUrl: '',
      points: [],
      display: { x: 0, y: 0, w: 1, h: 1 }
    };

    modal.rotateBtn.addEventListener('click', async () => {
      state.rotation = (state.rotation + 90) % 360;
      state.points = [];
      await renderPdfImportPreview(modal, state);
    });

    modal.clearBtn.addEventListener('click', () => {
      state.points = [];
      renderPdfImportCanvas(modal, state);
    });

    modal.cancelBtn.addEventListener('click', () => modal.root.remove());

    modal.canvas.addEventListener('click', evt => {
      const p = pdfPreviewPointFromEvent(modal, state, evt);
      if (!p) return;
      if (state.points.length >= 2) state.points = [];
      state.points.push(p);
      renderPdfImportCanvas(modal, state);
    });

    const insertFloorplan = () => {
      const refMm = Number(modal.measureInput.value);
      if (!Number.isFinite(refMm) || refMm <= 0) {
        modal.message.textContent = 'Bitte ein Referenzmass in mm eingeben.';
        return;
      }
      if (state.points.length !== 2) {
        modal.message.textContent = 'Bitte zwei Referenzpunkte in der Vorschau setzen.';
        return;
      }

      const dx = state.points[1].x - state.points[0].x;
      const dy = state.points[1].y - state.points[0].y;
      const pxLength = Math.hypot(dx, dy);
      if (pxLength <= 0.5) {
        modal.message.textContent = 'Die Referenzpunkte liegen zu nah beieinander.';
        return;
      }

      const mmPerPx = refMm / pxLength;
      const width = state.imageCanvas.width * mmPerPx;
      const height = state.imageCanvas.height * mmPerPx;

      setProjectFloorplan({
        kind: 'pdf',
        name: state.fileName,
        url: state.imageUrl,
        x: -width / 2,
        z: -height / 2,
        width,
        height,
        opacity: 0.5,
        rotation: state.rotation,
        referenceMm: refMm
      });
      modal.root.remove();
      AppController.refresh(false);
    };

    modal.insertBtn.addEventListener('click', insertFloorplan);
    modal.root.addEventListener('keydown', evt => {
      if (evt.key !== 'Enter') return;
      evt.preventDefault();
      insertFloorplan();
    });

    document.body.appendChild(modal.root);
    modal.measureInput.focus();
    await renderPdfImportPreview(modal, state);
}

function createPdfImportModal(fileName) {
    const root = document.createElement('div');
    root.className = 'modal-backdrop';
    root.innerHTML = `
      <div class="modal pdf-import-modal">
        <div class="modal-title">Grundriss importieren</div>
        <div class="small-text">${escapeHtml(fileName)}</div>
        <div class="pdf-preview-frame">
          <canvas class="pdf-preview-canvas" width="900" height="620"></canvas>
        </div>
        <div class="pdf-import-controls">
          <label>Referenzmass (mm)<input id="pdfReferenceMeasure" type="number" min="1" step="1" placeholder="z.B. 3000"></label>
          <button type="button" class="btn" data-action="rotate">90° drehen</button>
          <button type="button" class="btn" data-action="clear">Punkte löschen</button>
          <button type="button" class="btn primary" data-action="insert">Einfügen</button>
          <button type="button" class="btn" data-action="cancel">Abbrechen</button>
        </div>
        <div class="small-text note pdf-import-message">Zwei Punkte auf ein bekanntes Mass klicken.</div>
      </div>
    `;

    return {
      root,
      frame: root.querySelector('.pdf-preview-frame'),
      canvas: root.querySelector('.pdf-preview-canvas'),
      measureInput: root.querySelector('#pdfReferenceMeasure'),
      rotateBtn: root.querySelector('[data-action="rotate"]'),
      clearBtn: root.querySelector('[data-action="clear"]'),
      insertBtn: root.querySelector('[data-action="insert"]'),
      cancelBtn: root.querySelector('[data-action="cancel"]'),
      message: root.querySelector('.pdf-import-message')
    };
}

function stripHtml(value) {
    const doc = new DOMParser().parseFromString(String(value ?? ''), 'text/html');
    return doc.body.textContent || '';
}

async function renderPdfImportPreview(modal, state) {
    const viewport = state.page.getViewport({ scale: 2, rotation: state.rotation });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    await state.page.render({ canvasContext: ctx, viewport }).promise;

    state.imageCanvas = canvas;
    state.imageUrl = canvas.toDataURL('image/png');
    modal.message.textContent = `Ausrichtung ${state.rotation}° · zwei Referenzpunkte setzen.`;
    renderPdfImportCanvas(modal, state);
}

function renderPdfImportCanvas(modal, state) {
    const canvas = modal.canvas;
    syncPdfPreviewCanvasSize(modal, state);
    const ctx = canvas.getContext('2d');
    const img = state.imageCanvas;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!img) return;

    const x = 0;
    const y = 0;
    const w = canvas.width;
    const h = canvas.height;
    state.display = { x, y, w, h };

    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, x, y, w, h);
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, w, h);

    const screenPoints = state.points.map(p => ({
      x: x + p.x / img.width * w,
      y: y + p.y / img.height * h
    }));

    if (screenPoints.length === 2) {
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(screenPoints[0].x, screenPoints[0].y);
      ctx.lineTo(screenPoints[1].x, screenPoints[1].y);
      ctx.stroke();
    }

    for (const p of screenPoints) {
      ctx.fillStyle = '#dc2626';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
}

function syncPdfPreviewCanvasSize(modal, state) {
    const frameRect = modal.frame.getBoundingClientRect();
    const img = state.imageCanvas;
    if (!img) return;

    const scale = Math.min(frameRect.width / img.width, frameRect.height / img.height);
    const nextWidth = Math.max(1, Math.round(img.width * scale));
    const nextHeight = Math.max(1, Math.round(img.height * scale));
    const canvas = modal.canvas;

    if (canvas.width !== nextWidth) canvas.width = nextWidth;
    if (canvas.height !== nextHeight) canvas.height = nextHeight;
    canvas.style.width = `${nextWidth}px`;
    canvas.style.height = `${nextHeight}px`;
}

function pdfPreviewPointFromEvent(modal, state, evt) {
    const rect = modal.canvas.getBoundingClientRect();
    const sx = (evt.clientX - rect.left) * (modal.canvas.width / rect.width);
    const sy = (evt.clientY - rect.top) * (modal.canvas.height / rect.height);
    const d = state.display;
    if (sx < d.x || sx > d.x + d.w || sy < d.y || sy > d.y + d.h) return null;

    return {
      x: (sx - d.x) / d.w * state.imageCanvas.width,
      y: (sy - d.y) / d.h * state.imageCanvas.height
    };
}

function parseSatelliteCoordinateSearch(query) {
    const match = String(query || '').trim().match(/^\s*(-?\d+(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d+(?:[.,]\d+)?)\s*$/);
    if (!match) return null;
    const lat = Number(match[1].replace(',', '.'));
    const lng = Number(match[2].replace(',', '.'));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng, label: `${lat.toFixed(6)}, ${lng.toFixed(6)}` };
}

function createSatelliteImportModal() {
    const root = document.createElement('div');
    root.className = 'modal-backdrop';
    root.innerHTML = `
      <div class="modal satellite-map-modal">
        <div class="satellite-map-header">
          <div>
            <div class="modal-title">Satellitenkarte (swisstopo SWISSIMAGE)</div>
            <div class="small-text">Adresse, Ort oder GPS-Koordinaten suchen, Ausschnitt wählen und massstäblich übernehmen.</div>
          </div>
          <button type="button" class="btn" data-action="cancel">Abbrechen</button>
        </div>
        <div class="satellite-map-toolbar">
          <label>Suche
            <input type="text" data-satellite-search-input placeholder="Adresse, Ort oder 47.3769, 8.5417">
          </label>
          <div class="satellite-map-mode-toggle" role="group" aria-label="Kartendarstellung">
            <button type="button" class="btn is-active" data-satellite-map-mode="satellite">Satellit</button>
            <button type="button" class="btn" data-satellite-map-mode="map">Karte</button>
          </div>
          <button type="button" class="btn" data-action="search">Suchen</button>
          <button type="button" class="btn" data-action="select">Kartenausschnitt übernehmen</button>
          <button type="button" class="btn" data-action="clear-selection">Auswahl löschen</button>
          <button type="button" class="btn primary" data-action="insert">Einfügen</button>
        </div>
        <div class="satellite-map-results" data-satellite-results></div>
        <div class="satellite-map-frame" data-satellite-map tabindex="0" aria-label="Karte">
          <div class="satellite-map-tile-layer" data-satellite-tiles></div>
          <div class="satellite-map-selection-layer" data-satellite-selection></div>
          <div class="satellite-map-controls" aria-label="Kartenzoom">
            <button type="button" class="btn" data-action="zoom-in" title="Vergrössern">+</button>
            <button type="button" class="btn" data-action="zoom-out" title="Verkleinern">-</button>
          </div>
          <div class="satellite-map-attribution" data-satellite-attribution>swisstopo SWISSIMAGE</div>
        </div>
        <div class="small-text note satellite-map-message" data-satellite-message>Zur gewünschten Position navigieren oder suchen.</div>
      </div>
    `;

    return {
      root,
      map: root.querySelector('[data-satellite-map]'),
      tiles: root.querySelector('[data-satellite-tiles]'),
      selection: root.querySelector('[data-satellite-selection]'),
      message: root.querySelector('[data-satellite-message]'),
      results: root.querySelector('[data-satellite-results]'),
      searchInput: root.querySelector('[data-satellite-search-input]'),
      mapModeButtons: Array.from(root.querySelectorAll('[data-satellite-map-mode]')),
      attribution: root.querySelector('[data-satellite-attribution]'),
      searchBtn: root.querySelector('[data-action="search"]'),
      selectBtn: root.querySelector('[data-action="select"]'),
      clearSelectionBtn: root.querySelector('[data-action="clear-selection"]'),
      insertBtn: root.querySelector('[data-action="insert"]'),
      zoomInBtn: root.querySelector('[data-action="zoom-in"]'),
      zoomOutBtn: root.querySelector('[data-action="zoom-out"]'),
      cancelBtn: root.querySelector('[data-action="cancel"]')
    };
}

function openSatelliteMapImport() {
    const modal = createSatelliteImportModal();
    const state = {
      center: {
        lat: DEFAULT_SATELLITE_MAP_LOCATION.lat,
        lng: DEFAULT_SATELLITE_MAP_LOCATION.lng
      },
      zoom: DEFAULT_SATELLITE_MAP_LOCATION.zoom,
      mapMode: SATELLITE_MAP_MODES.SATELLITE,
      selectionMode: false,
      selectionRect: null,
      selectionCorners: null,
      pan: null,
      selectionDrag: null,
      wheelZoomDelta: 0
    };

    const setMessage = text => {
      modal.message.textContent = text;
    };
    const render = () => renderSatelliteMap(modal, state);
    const close = () => modal.root.remove();

    modal.cancelBtn.addEventListener('click', close);
    modal.searchBtn.addEventListener('click', () => searchSatelliteLocation(modal, state, render));
    modal.searchInput.addEventListener('keydown', event => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      searchSatelliteLocation(modal, state, render);
    });
    modal.mapModeButtons.forEach(button => {
      button.addEventListener('click', () => {
        state.mapMode = normalizeSatelliteMapMode(button.dataset.satelliteMapMode);
        updateSatelliteMapModeUi(modal, state);
        render();
      });
    });
    modal.selectBtn.addEventListener('click', () => {
      state.selectionMode = !state.selectionMode;
      if (state.selectionMode) {
        state.selectionRect = null;
        state.selectionCorners = null;
        setMessage('Rechteck über den gewünschten Kartenausschnitt ziehen.');
      } else {
        setMessage('Kartenauswahl beendet.');
      }
      modal.selectBtn.classList.toggle('is-active', state.selectionMode);
      renderSatelliteSelection(modal, state);
    });
    modal.clearSelectionBtn.addEventListener('click', () => {
      clearSatelliteSelection(modal, state);
      setMessage('Auswahl gelöscht.');
    });
    modal.insertBtn.addEventListener('click', () => insertSatelliteFloorplan(modal, state));
    modal.zoomInBtn.addEventListener('click', () => {
      clearSatelliteSelection(modal, state);
      state.zoom = clampSatelliteZoom(state.zoom + 1);
      render();
    });
    modal.zoomOutBtn.addEventListener('click', () => {
      clearSatelliteSelection(modal, state);
      state.zoom = clampSatelliteZoom(state.zoom - 1);
      render();
    });
    modal.map.addEventListener('mousedown', event => handleSatelliteMapMouseDown(event, modal, state, render));
    modal.map.addEventListener('wheel', event => handleSatelliteMapWheel(event, modal, state, render), { passive: false });
    document.body.appendChild(modal.root);
    modal.searchInput.value = DEFAULT_SATELLITE_MAP_LOCATION.label;
    updateSatelliteMapModeUi(modal, state);
    modal.searchInput.focus();
    requestAnimationFrame(render);
}

function updateSatelliteMapModeUi(modal, state) {
    const mode = normalizeSatelliteMapMode(state.mapMode);
    state.mapMode = mode;
    modal.map.dataset.satelliteMapMode = mode;
    modal.attribution.textContent = satelliteMapAttribution(mode);
    for (const button of modal.mapModeButtons) {
      button.classList.toggle('is-active', normalizeSatelliteMapMode(button.dataset.satelliteMapMode) === mode);
    }
}

async function searchSatelliteLocation(modal, state, render) {
    const query = String(modal.searchInput.value || '').trim();
    if (!query) {
      modal.message.textContent = 'Bitte Adresse, Ort oder Koordinaten eingeben.';
      return;
    }

    const coordinate = parseSatelliteCoordinateSearch(query);
    if (coordinate) {
      clearSatelliteSelection(modal, state);
      state.center = { lat: coordinate.lat, lng: coordinate.lng };
      state.zoom = Math.max(state.zoom, 20);
      modal.results.innerHTML = '';
      modal.message.textContent = `Position ${coordinate.label} geladen.`;
      render();
      return;
    }

    modal.message.textContent = 'Suche läuft...';
    modal.results.innerHTML = '';
    try {
      const response = await fetch(swisstopoSearchUrl(query), { cache: 'no-cache' });
      if (!response.ok) throw new Error(`GeoAdmin-Suche antwortet mit ${response.status}`);
      const data = await response.json();
      const results = Array.isArray(data?.results) ? data.results : [];
      if (!results.length) {
        modal.message.textContent = 'Keine Treffer gefunden.';
        return;
      }
      modal.results.innerHTML = results.map((result, index) => `
        <button type="button" class="satellite-map-result" data-result-index="${index}">
          ${escapeHtml(stripHtml(result.attrs?.label || result.attrs?.detail || 'Treffer'))}
        </button>
      `).join('');
      modal.results.querySelectorAll('[data-result-index]').forEach(button => {
        button.addEventListener('click', () => {
          const result = results[Number(button.getAttribute('data-result-index'))];
          const attrs = result?.attrs || {};
          const lat = Number(attrs.lat);
          const lng = Number(attrs.lon);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
          clearSatelliteSelection(modal, state);
          state.center = { lat, lng };
          state.zoom = Math.max(state.zoom, 20);
          modal.message.textContent = `${stripHtml(attrs.label || attrs.detail || 'Treffer')} geladen.`;
          render();
        });
      });
      modal.message.textContent = 'Treffer auswählen oder Karte verschieben.';
    } catch (error) {
      modal.message.textContent = `Suche fehlgeschlagen: ${error.message || error}`;
    }
}

function renderSatelliteMap(modal, state) {
    const rect = modal.map.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    const zoom = clampSatelliteZoom(state.zoom);
    const mapMode = normalizeSatelliteMapMode(state.mapMode);
    const sourceZoom = Math.min(zoom, satelliteMapTileMaxZoom(mapMode));
    const sourceScale = 2 ** (zoom - sourceZoom);
    state.zoom = zoom;
    const centerPx = lonLatToWorldPixel(state.center.lat, state.center.lng, zoom);
    const startX = centerPx.x - width / 2;
    const startY = centerPx.y - height / 2;
    const endX = centerPx.x + width / 2;
    const endY = centerPx.y + height / 2;
    const sourceStartX = startX / sourceScale;
    const sourceStartY = startY / sourceScale;
    const sourceEndX = endX / sourceScale;
    const sourceEndY = endY / sourceScale;
    const displayTileSize = SATELLITE_TILE_SIZE_PX * sourceScale;
    const minTileX = Math.floor(sourceStartX / SATELLITE_TILE_SIZE_PX);
    const maxTileX = Math.floor(sourceEndX / SATELLITE_TILE_SIZE_PX);
    const minTileY = Math.floor(sourceStartY / SATELLITE_TILE_SIZE_PX);
    const maxTileY = Math.floor(sourceEndY / SATELLITE_TILE_SIZE_PX);
    const tiles = 2 ** sourceZoom;
    const visibleTiles = new Map();

    for (let tileY = minTileY; tileY <= maxTileY; tileY += 1) {
      if (tileY < 0 || tileY >= tiles) continue;
      for (let tileX = minTileX; tileX <= maxTileX; tileX += 1) {
        if (!satelliteMapTileAvailable(mapMode, sourceZoom, tileX, tileY)) continue;
        const left = Math.round(tileX * displayTileSize - startX);
        const top = Math.round(tileY * displayTileSize - startY);
        const key = `${mapMode}:${sourceZoom}:${tileX}:${tileY}`;
        visibleTiles.set(key, {
          key,
          src: satelliteMapTileUrl(mapMode, sourceZoom, tileX, tileY),
          left,
          top,
          size: displayTileSize
        });
      }
    }

    syncSatelliteTileElements(modal.tiles, visibleTiles);
    renderSatelliteSelection(modal, state);
}

function syncSatelliteTileElements(tileLayer, visibleTiles) {
    if (!tileLayer) return;
    const existing = new Map();
    tileLayer.querySelectorAll('img[data-satellite-tile-key]').forEach(img => {
      existing.set(img.dataset.satelliteTileKey, img);
    });

    for (const [key, tile] of visibleTiles.entries()) {
      let img = existing.get(key);
      if (!img) {
        img = document.createElement('img');
        img.alt = '';
        img.crossOrigin = 'anonymous';
        img.draggable = false;
        img.dataset.satelliteTileKey = key;
        img.src = tile.src;
        tileLayer.appendChild(img);
      }
      img.style.left = `${tile.left}px`;
      img.style.top = `${tile.top}px`;
      img.style.width = `${tile.size}px`;
      img.style.height = `${tile.size}px`;
      existing.delete(key);
    }

    for (const img of existing.values()) {
      img.remove();
    }
}

function handleSatelliteMapMouseDown(event, modal, state, render) {
    if (event.button !== 0) return;
    event.preventDefault();
    const mapRect = modal.map.getBoundingClientRect();
    const start = {
      x: event.clientX - mapRect.left,
      y: event.clientY - mapRect.top
    };
    if (state.selectionMode) {
      state.selectionDrag = { start, current: start };
      updateSatelliteSelectionFromDrag(modal, state);
    } else {
      const centerPx = lonLatToWorldPixel(state.center.lat, state.center.lng, state.zoom);
      state.pan = {
        startScreen: { x: event.clientX, y: event.clientY },
        startCenterPx: centerPx
      };
    }

    const move = moveEvent => {
      if (state.selectionDrag) {
        const currentMapRect = modal.map.getBoundingClientRect();
        state.selectionDrag.current = {
          x: moveEvent.clientX - currentMapRect.left,
          y: moveEvent.clientY - currentMapRect.top
        };
        updateSatelliteSelectionFromDrag(modal, state);
        return;
      }
      if (!state.pan) return;
      clearSatelliteSelection(modal, state);
      const dx = moveEvent.clientX - state.pan.startScreen.x;
      const dy = moveEvent.clientY - state.pan.startScreen.y;
      const next = worldPixelToLonLat(
        state.pan.startCenterPx.x - dx,
        state.pan.startCenterPx.y - dy,
        state.zoom
      );
      state.center = next;
      render();
    };
    const up = () => {
      if (state.selectionDrag) {
        updateSatelliteSelectionFromDrag(modal, state, true);
        state.selectionDrag = null;
      }
      state.pan = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
}

function handleSatelliteMapWheel(event, modal, state, render) {
    event.preventDefault();
    const previousZoom = state.zoom;
    const unitScale = event.deltaMode === 1 ? 40 : (event.deltaMode === 2 ? 240 : 1);
    const normalizedDelta = Math.max(-120, Math.min(120, Number(event.deltaY) * unitScale));
    let zoomDirection;
    if (Math.abs(normalizedDelta) >= SATELLITE_WHEEL_NOTCH_DELTA) {
      zoomDirection = normalizedDelta < 0 ? 1 : -1;
      state.wheelZoomDelta = 0;
    } else {
      state.wheelZoomDelta = (Number(state.wheelZoomDelta) || 0) + normalizedDelta;
      if (Math.abs(state.wheelZoomDelta) < SATELLITE_FINE_WHEEL_ZOOM_THRESHOLD) return;
      zoomDirection = state.wheelZoomDelta < 0 ? 1 : -1;
      state.wheelZoomDelta -= Math.sign(state.wheelZoomDelta) * SATELLITE_FINE_WHEEL_ZOOM_THRESHOLD;
    }
    const nextZoom = clampSatelliteZoom(previousZoom + zoomDirection);
    if (nextZoom === previousZoom) return;
    clearSatelliteSelection(modal, state);
    const rect = modal.map.getBoundingClientRect();
    const mouse = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    const beforeCenter = lonLatToWorldPixel(state.center.lat, state.center.lng, previousZoom);
    const beforeMouseWorld = {
      x: beforeCenter.x - rect.width / 2 + mouse.x,
      y: beforeCenter.y - rect.height / 2 + mouse.y
    };
    const fixedLonLat = worldPixelToLonLat(beforeMouseWorld.x, beforeMouseWorld.y, previousZoom);
    const fixedAfter = lonLatToWorldPixel(fixedLonLat.lat, fixedLonLat.lng, nextZoom);
    const nextCenterWorld = {
      x: fixedAfter.x - mouse.x + rect.width / 2,
      y: fixedAfter.y - mouse.y + rect.height / 2
    };
    state.zoom = nextZoom;
    state.center = worldPixelToLonLat(nextCenterWorld.x, nextCenterWorld.y, nextZoom);
    render();
}

function clearSatelliteSelection(modal, state) {
    state.selectionRect = null;
    state.selectionCorners = null;
    state.selectionDrag = null;
    renderSatelliteSelection(modal, state);
}

function updateSatelliteSelectionFromDrag(modal, state, final = false) {
    const drag = state.selectionDrag;
    if (!drag) return;
    const rect = modal.map.getBoundingClientRect();
    const minX = Math.max(0, Math.min(drag.start.x, drag.current.x));
    const maxX = Math.min(rect.width, Math.max(drag.start.x, drag.current.x));
    const minY = Math.max(0, Math.min(drag.start.y, drag.current.y));
    const maxY = Math.min(rect.height, Math.max(drag.start.y, drag.current.y));
    if (Math.abs(maxX - minX) < 4 || Math.abs(maxY - minY) < 4) {
      state.selectionRect = null;
      state.selectionCorners = null;
      renderSatelliteSelection(modal, state);
      return;
    }
    state.selectionRect = {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
    state.selectionCorners = satelliteSelectionCornersFromScreenRect(state, rect, state.selectionRect);
    renderSatelliteSelection(modal, state);
    if (final && state.selectionCorners) {
      const fp = createSatelliteFloorplan(state.selectionCorners);
      modal.message.textContent = `Ausschnitt: ${Math.round(fp.realWidthMm / 10) / 100} m × ${Math.round(fp.realHeightMm / 10) / 100} m`;
    }
}

function satelliteSelectionCornersFromScreenRect(state, mapRect, selectionRect) {
    const zoom = clampSatelliteZoom(state.zoom);
    const centerPx = lonLatToWorldPixel(state.center.lat, state.center.lng, zoom);
    const startX = centerPx.x - mapRect.width / 2;
    const startY = centerPx.y - mapRect.height / 2;
    return {
      northWest: worldPixelToLonLat(startX + selectionRect.x, startY + selectionRect.y, zoom),
      northEast: worldPixelToLonLat(startX + selectionRect.x + selectionRect.width, startY + selectionRect.y, zoom),
      southWest: worldPixelToLonLat(startX + selectionRect.x, startY + selectionRect.y + selectionRect.height, zoom),
      southEast: worldPixelToLonLat(startX + selectionRect.x + selectionRect.width, startY + selectionRect.y + selectionRect.height, zoom)
    };
}

function renderSatelliteSelection(modal, state) {
    if (!state.selectionRect) {
      modal.selection.innerHTML = '';
      return;
    }
    const rect = state.selectionRect;
    modal.selection.innerHTML = `
      <div
        class="satellite-map-selection-rect"
        style="left:${rect.x}px;top:${rect.y}px;width:${rect.width}px;height:${rect.height}px"
      ></div>
    `;
}

async function insertSatelliteFloorplan(modal, state) {
    if (!state.selectionCorners) {
      modal.message.textContent = 'Bitte zuerst mit "Kartenausschnitt übernehmen" ein Rechteck ziehen.';
      return;
    }
    const floorplan = createSatelliteFloorplan(state.selectionCorners, {
      mapMode: state.mapMode,
      provider: normalizeSatelliteMapMode(state.mapMode) === SATELLITE_MAP_MODES.MAP ? 'osm' : 'swisstopo',
      name: normalizeSatelliteMapMode(state.mapMode) === SATELLITE_MAP_MODES.MAP
        ? 'Kartenansicht'
        : 'Satellitenkarte (swisstopo SWISSIMAGE)',
      opacity: 0.85,
      locked: true
    });
    const sourceUrl = floorplan.url;
    modal.insertBtn.disabled = true;
    modal.message.textContent = 'Kartenausschnitt wird geladen...';
    try {
      floorplan.sourceUrl = sourceUrl;
      floorplan.url = await createSatelliteSelectionSnapshotDataUrl(state, floorplan);
    } catch (error) {
      console.warn('[KS Studio] Satellitenkarte konnte nicht als lokale Bilddaten übernommen werden.', error);
      modal.insertBtn.disabled = false;
      modal.message.textContent = `Kartenausschnitt konnte nicht übernommen werden: ${error.message || error}`;
      return;
    }
    setProjectFloorplan(floorplan);
    modal.root.remove();
    setStatus(`Kartenausschnitt eingefügt · ${Math.round(floorplan.realWidthMm)} × ${Math.round(floorplan.realHeightMm)} mm`);
    AppController.refresh(false);
}

function isFloorplanSatellite(fp = S.project.floorplan) {
    return String(fp?.kind || fp?.backgroundType || '').trim().toLowerCase() === 'satellite';
}

function isFloorplanInteractionTarget(target) {
    return Boolean(target?.closest?.('[data-floorplan-background="true"]'));
}

function startFloorplanDrag(event) {
    const fp = S.project.floorplan;
    if (!fp || fp.visible === false || fp.locked === true) return false;
    if (isFloorplanSatellite(fp)) return false;
    if (!isFloorplanInteractionTarget(event.target)) return false;
    const svg = document.getElementById('planSvg');
    const rect = svg.getBoundingClientRect();
    const world = screenToWorld(event.clientX - rect.left, event.clientY - rect.top);
    event.preventDefault();
    S.drag = {
      active: true,
      type: 'floorplan',
      startMouse: world,
      startFloorplan: { x: fp.x, z: fp.z },
      hasMoved: false
    };
    setStatus('Hintergrund verschieben');
    return true;
}

function handleFloorplanDragMove(world) {
    const fp = S.project.floorplan;
    const drag = S.drag;
    if (!fp || !drag?.active || drag.type !== 'floorplan') return false;
    const dx = world.x - drag.startMouse.x;
    const dz = world.z - drag.startMouse.z;
    if (Math.hypot(dx, dz) > 0.001) drag.hasMoved = true;
    fp.x = drag.startFloorplan.x + dx;
    fp.z = drag.startFloorplan.z + dz;
    AppController.requestPlanRender('floorplan-drag');
    return true;
}

function finishFloorplanDrag() {
    if (S.drag?.type !== 'floorplan') return false;
    const moved = Boolean(S.drag.hasMoved);
    if (moved) markProjectFloorplanChanged('floorplan-move');
    setStatus(moved ? 'Hintergrund verschoben' : 'Bereit');
    return moved;
}

async function renderPdfFirstPage(url) {
    const pdfjs = await getPdfLibrary();
    const loadingTask = pdfjs.getDocument(url);
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: ctx, viewport }).promise;
    return canvas.toDataURL('image/png');
}

export {
  bindFloorplanImport,
  importFloorplanFile,
  importCadFloorplanFile,
  fileToDataUrl,
  startFloorplanRemeasure,
  cancelFloorplanRemeasure,
  handleFloorplanMeasureClick,
  pointInsideFloorplan,
  applyFloorplanRemeasure,
  openPdfFloorplanImport,
  createPdfImportModal,
  openCadFloorplanImport,
  createCadImportModal,
  renderPdfImportPreview,
  renderPdfImportCanvas,
  syncPdfPreviewCanvasSize,
  pdfPreviewPointFromEvent,
  openSatelliteMapImport,
  parseSatelliteCoordinateSearch,
  createSatelliteImportModal,
  renderSatelliteMap,
  searchSatelliteLocation,
  isFloorplanSatellite,
  startFloorplanDrag,
  handleFloorplanDragMove,
  finishFloorplanDrag,
  renderPdfFirstPage,
  getPdfLibrary
};
